import { useEffect, useState } from "react";

/**
 * Tiniest possible client-side router. Hash-based — no library, no history-API
 * juggling, no SSR concerns. URLs look like `#/overview`, `#/pipeline`, etc.
 *
 * For ≤8 routes this stays simple. When we add nested routes (keyword detail,
 * brief editor, etc.) we'll graduate to react-router or wouter.
 */

/**
 * ONE list. The type is derived from the runtime array, not written alongside
 * it — they were two separate lists, and adding "automations" to the type while
 * missing the array produced a route that typechecked, highlighted in the nav,
 * set the hash, and silently rendered Overview. Nothing could catch it.
 */
const VALID = [
  "overview", "tasks", "sources", "research", "pipeline", "content", "content-db",
  "entities", "distribution", "calendar", "video", "audience", "audit", "automations", "models", "folded", "offerings", "credentials",
  "errors", "spend", "brand", "help",
] as const;

export type Route = (typeof VALID)[number];

/**
 * A navigation target: a route, optionally carrying a query string.
 *
 * Deep links (`tasks?open=<keywordId>`, `pipeline?open=<keywordId>`) already
 * worked, but only by accident — `setRoute` interpolated whatever string it was
 * handed and `parseRoute` happened to split the query off. That is a behaviour
 * nobody could rely on deliberately, and callers were casting through `as Route`
 * to get at it. Naming the shape makes the deep link a supported thing rather
 * than one refactor away from breaking.
 */
export type RouteTarget = Route | `${Route}?${string}`;

function parseRoute(hash: string): Route {
  const clean = hash.replace(/^#\/?/, "").split(/[/?]/)[0] as Route | "";
  if ((VALID as readonly string[]).includes(clean)) return clean as Route;
  return "overview";
}

/** The `?…` part of the current hash, for pages that read a deep-link param. */
export function routeQuery(): URLSearchParams {
  return new URLSearchParams(window.location.hash.split("?")[1] ?? "");
}

export function useRoute(): [Route, (next: RouteTarget) => void] {
  const [route, setRouteState] = useState<Route>(() => parseRoute(window.location.hash));

  useEffect(() => {
    const onChange = () => setRouteState(parseRoute(window.location.hash));
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);

  const setRoute = (next: RouteTarget) => {
    window.location.hash = `/${next}`;
  };

  return [route, setRoute];
}
